import React, { useState } from 'react'
import { usePage, router } from '@inertiajs/react'
import type { PageProps } from '@/types'
import { ShieldCheck, AlertCircle, CheckCircle2, Loader2, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { KatexRenderer } from '@/components/shared/KatexRenderer'

export function TermsAcceptanceModal() {
  const { props } = usePage<PageProps>()
  const pendingTerms = props.pending_terms

  const [agreed, setAgreed] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  if (!pendingTerms) {
    return null
  }

  const handleAccept = (e: React.FormEvent) => {
    e.preventDefault()
    if (!agreed || submitting) return

    setSubmitting(true)
    router.post(
      '/terms/accept',
      {},
      {
        preserveScroll: true,
        onFinish: () => {
          setSubmitting(false)
        },
      }
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-background/80 backdrop-blur-md select-none"
    >
      <div className="bg-card border border-border shadow-2xl rounded-xl sm:rounded-2xl max-w-3xl w-full h-[94vh] sm:h-auto sm:max-h-[88vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-3.5 py-3 sm:px-6 sm:py-4 border-b border-border bg-muted/40 flex items-start justify-between gap-3 shrink-0">
          <div className="flex items-start gap-2.5 sm:gap-3.5">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0 mt-0.5">
              <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div>
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <h2 id="terms-modal-title" className="text-sm sm:text-base font-bold text-foreground tracking-tight leading-tight">
                  {pendingTerms.title || 'Terms of Service & License Agreement'}
                </h2>
                <span className="text-[10px] sm:text-xs font-semibold px-1.5 sm:px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 shrink-0">
                  v{pendingTerms.version_number}
                </span>
              </div>
              <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-tight">
                Please review and accept our terms to continue using PrepKiya.
                {pendingTerms.activated_at && ` Effective: ${pendingTerms.activated_at}.`}
              </p>
            </div>
          </div>
        </div>

        {/* Notice Banner */}
        <div className="px-3.5 py-2 sm:px-6 sm:py-2.5 bg-muted/70 border-b border-border text-[10px] sm:text-xs text-muted-foreground flex items-center gap-2 shrink-0">
          <AlertCircle className="w-3.5 h-3.5 text-primary shrink-0" />
          <span className="leading-snug">
            PrepKiya is strictly for <strong>educational and preparation purposes</strong>. All content is AI-generated &quot;as-is&quot; without warranties.
          </span>
        </div>

        {/* Scrollable Terms Content - dynamically occupies all available viewport height */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3.5 sm:p-6 select-text border-b border-border bg-card space-y-3 sm:space-y-4">
          {pendingTerms.summary && (
            <div className="p-3 sm:p-3.5 rounded-lg sm:rounded-xl bg-muted/60 border border-border text-[11px] sm:text-xs text-muted-foreground space-y-0.5">
              <p className="font-semibold text-foreground">Summary of Terms:</p>
              <p>{pendingTerms.summary}</p>
            </div>
          )}

          <div className="text-foreground/90">
            <KatexRenderer
              content={pendingTerms.content}
              format="markdown"
              disableExamHelpers
              className="markdown-content text-xs sm:text-sm leading-relaxed"
            />
          </div>
        </div>

        {/* Footer with Mandatory Checkbox and Submit */}
        <form onSubmit={handleAccept} className="p-3 sm:p-5 bg-muted/40 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 shrink-0">
          <div className="flex items-start gap-2.5 select-none max-w-lg">
            <Checkbox
              id="accept-terms-checkbox"
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked === true)}
              className="mt-0.5"
            />
            <label
              htmlFor="accept-terms-checkbox"
              className="text-[11px] sm:text-xs text-muted-foreground leading-tight sm:leading-normal cursor-pointer"
            >
              I have read, understood, and agree to be legally bound by the PrepKiya Terms of Service and End-User License Agreement.
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 shrink-0">
            <Button
              type="submit"
              disabled={!agreed || submitting}
              className="w-full sm:w-auto font-semibold shadow-xs flex items-center justify-center gap-2 h-9 sm:h-10 text-xs sm:text-sm"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>Accept & Continue</span>
                  <ArrowRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 ml-0.5" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
